import type { Weather } from "../lib/weather";
import { weatherLabel } from "../lib/weather";

type Props = {
  weather: Weather | null;
};

/**
 * Weather panel — replaces the static brand area in the top bar.
 * Renders an icon + temperature + a tight one-line caption (condition,
 * feels-like, wind).
 */
export function WeatherPanel({ weather }: Props) {
  const temp = weather?.tempC;
  const condition = weatherLabel(weather?.code ?? null, weather?.isDay ?? true);
  const feels = weather?.feelsC;
  const wind = weather?.windKmh;

  return (
    <div className="weather">
      <div className="weather-icon-wrap">
        <WeatherIcon code={weather?.code ?? null} isDay={weather?.isDay ?? true} />
      </div>
      <div className="weather-text">
        <div className="weather-temp tabular">
          {temp != null ? Math.round(temp) : "—"}
          <span className="weather-temp-unit">°</span>
        </div>
        <div className="weather-caption">
          <span className="weather-cond">{condition}</span>
          {feels != null && (
            <span className="weather-meta"> · føles som {Math.round(feels)}°</span>
          )}
          {wind != null && (
            <span className="weather-meta"> · {Math.round(wind)} km/t</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* =====================================================================
   Weather icon set. SVG so it scales cleanly and inherits the accent
   colour. Designed as a small, expressive glyph family — sun + moon
   share the same disc, clouds share the same body, rain/snow stack on.
   ===================================================================== */
function WeatherIcon({ code, isDay }: { code: number | null; isDay: boolean }) {
  if (code == null) return <Disc />;
  if (code === 0) return isDay ? <Sun /> : <Moon />;
  if (code === 1 || code === 2) return isDay ? <SunCloud /> : <MoonCloud />;
  if (code === 3) return <Cloud />;
  if (code === 45 || code === 48) return <Fog />;
  if (code >= 51 && code <= 57) return <Drizzle />;
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return <Rain />;
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return <Snow />;
  if (code >= 95) return <Thunder />;
  return <Cloud />;
}

const COMMON = {
  width: 56,
  height: 56,
  viewBox: "0 0 64 64",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2.2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

function Disc() {
  return (
    <svg {...COMMON} aria-hidden>
      <circle cx="32" cy="32" r="9" fill="currentColor" opacity="0.6" />
    </svg>
  );
}

function Sun() {
  return (
    <svg {...COMMON} aria-hidden>
      <circle cx="32" cy="32" r="10" fill="currentColor" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
        const rad = (deg * Math.PI) / 180;
        const x1 = 32 + Math.cos(rad) * 18;
        const y1 = 32 + Math.sin(rad) * 18;
        const x2 = 32 + Math.cos(rad) * 26;
        const y2 = 32 + Math.sin(rad) * 26;
        return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} />;
      })}
    </svg>
  );
}

function Moon() {
  return (
    <svg {...COMMON} aria-hidden>
      <path d="M40 14a16 16 0 1 0 12 28 14 14 0 0 1-12-28z" fill="currentColor" />
      {/* tiny stars */}
      <circle cx="18" cy="20" r="1.2" fill="currentColor" opacity="0.7" />
      <circle cx="22" cy="44" r="1" fill="currentColor" opacity="0.55" />
      <circle cx="14" cy="34" r="0.9" fill="currentColor" opacity="0.45" />
    </svg>
  );
}

function CloudBody(props: { y?: number; opacity?: number; fill?: string }) {
  const y = props.y ?? 28;
  return (
    <path
      d={`M18 ${y + 12} a8 8 0 0 1 4-15.5 11 11 0 0 1 21 2.5 7 7 0 0 1-1 13.9z`}
      fill={props.fill ?? "currentColor"}
      opacity={props.opacity ?? 1}
    />
  );
}

function Cloud() {
  return (
    <svg {...COMMON} aria-hidden>
      <CloudBody />
    </svg>
  );
}

function SunCloud() {
  return (
    <svg {...COMMON} aria-hidden>
      <circle cx="22" cy="22" r="7" fill="currentColor" />
      {[-90, -45, 0, 45].map((deg) => {
        const rad = (deg * Math.PI) / 180;
        const x1 = 22 + Math.cos(rad) * 11;
        const y1 = 22 + Math.sin(rad) * 11;
        const x2 = 22 + Math.cos(rad) * 16;
        const y2 = 22 + Math.sin(rad) * 16;
        return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} />;
      })}
      <CloudBody y={32} />
    </svg>
  );
}

function MoonCloud() {
  return (
    <svg {...COMMON} aria-hidden>
      <path d="M30 12a11 11 0 1 0 8 19 10 10 0 0 1-8-19z" fill="currentColor" />
      <CloudBody y={32} />
    </svg>
  );
}

function Drizzle() {
  return (
    <svg {...COMMON} aria-hidden>
      <CloudBody y={22} />
      {[24, 34, 44].map((x, i) => (
        <line key={x} x1={x} y1={48 + (i % 2) * 2} x2={x - 2} y2={56 + (i % 2) * 2} />
      ))}
    </svg>
  );
}

function Rain() {
  return (
    <svg {...COMMON} aria-hidden>
      <CloudBody y={20} />
      {[22, 32, 42].map((x, i) => (
        <line
          key={x}
          x1={x}
          y1={46 + (i % 2) * 2}
          x2={x - 4}
          y2={58 + (i % 2) * 2}
          strokeWidth={2.6}
        />
      ))}
    </svg>
  );
}

function Snow() {
  return (
    <svg {...COMMON} aria-hidden>
      <CloudBody y={20} />
      {[
        [22, 50],
        [32, 54],
        [42, 50],
      ].map(([x, y]) => (
        <g key={`${x}-${y}`} transform={`translate(${x} ${y})`}>
          <line x1={-4} y1={0} x2={4} y2={0} />
          <line x1={0} y1={-4} x2={0} y2={4} />
          <line x1={-3} y1={-3} x2={3} y2={3} />
          <line x1={-3} y1={3} x2={3} y2={-3} />
        </g>
      ))}
    </svg>
  );
}

function Fog() {
  return (
    <svg {...COMMON} aria-hidden>
      <CloudBody y={14} opacity={0.7} />
      <line x1={14} y1={44} x2={50} y2={44} strokeWidth={2.6} />
      <line x1={18} y1={50} x2={46} y2={50} strokeWidth={2.6} />
      <line x1={14} y1={56} x2={50} y2={56} strokeWidth={2.6} />
    </svg>
  );
}

function Thunder() {
  return (
    <svg {...COMMON} aria-hidden>
      <CloudBody y={18} />
      <path
        d="M32 40 L26 52 L31 52 L28 60 L36 48 L31 48 L34 40 Z"
        fill="currentColor"
      />
    </svg>
  );
}
